/**
 * 화면 설정 — 왼쪽 메뉴와 오른쪽 항목.
 *
 * **기기 정비 화면과 같은 이름으로 나눈다.** 현장 기술자는 기기에서 [소프트웨어]·[전원·시작] 을
 * 보고, 운영자는 콘솔에서 같은 이름을 본다. 전화로 "전원·시작에서 자동 실행" 이라고 하면 양쪽이
 * 같은 자리를 짚는다.
 *
 *   소프트웨어   지금 버전·목표 버전, 업데이트 채널, 버전 고정, 자동 업데이트
 *   화면 표시    화면 회전, 운영 시간(환경 설정 따르기·늘 켜기·이 화면만)
 *   전원·시작    부팅 시 자동 실행, 원격 명령(재부팅·앱 종료·전원 종료)
 *   정비 PIN     현장에서 정비 화면을 열 때 넣는 PIN — 사이드바의 공급사 메뉴 "정비 모드" 와 헷갈리지
 *                않게 메뉴 이름은 PIN 으로 부른다
 *
 * 예전 탭은 [업데이트](채널·회전·버전 고정)·[정비 모드 PIN]·[원격 설정](자동 업데이트·자동 실행·
 * 명령)이었다 — 업데이트와 상관없는 회전이 업데이트 탭에, 자동 업데이트는 다른 탭에 있었다.
 *
 * 고르는 즉시 저장한다(지금까지와 같다). 두세 갈래는 구간 선택으로 늘 보이게 두고, 고를 것이 많은
 * 버전 고정만 펼침 목록이다.
 *
 * **할 수 없는 항목은 내지 않는다.** 채널·버전 고정·회전은 `schedule:write`(PATCH /displays/:id),
 * 자동 업데이트·자동 실행·명령·PIN 은 `fleet:write` 다 — 서버가 그렇게 검사한다.
 */
import { useEffect, useState, type KeyboardEvent, type ReactNode } from 'react';
import { KeyRound, MonitorCog, PackageCheck, Power, RectangleHorizontal, RectangleVertical } from 'lucide-react';
import type { AgentCommandKind, Display } from '../api/client.js';
import { behindReason } from '../agent-status.js';
import type { VersionOption } from '../releases.js';
import { AgentOutdatedNotice, ChoiceSegment, RemoteCommands, reportedText } from './AgentControlForm.js';
import { Disclosure } from './Disclosure.js';
import { HelpTip } from './HelpTip.js';
import { HoursRulesEditor, type DaypartInput } from './HoursEditor.js';
import { MaintenancePinForm } from './MaintenancePinForm.js';
import { Badge } from './ui.js';

export type SettingsSection = 'software' | 'display' | 'power' | 'maintenance';

/** 메뉴 — 순서가 곧 화면의 순서다. `needs` 중 하나라도 있으면 연다. */
export const SETTINGS_SECTIONS: ReadonlyArray<{
  id: SettingsSection;
  label: string;
  icon: typeof PackageCheck;
  needs: readonly string[];
}> = [
  { id: 'software', label: '소프트웨어', icon: PackageCheck, needs: ['schedule:write', 'fleet:write'] },
  { id: 'display', label: '화면 표시', icon: MonitorCog, needs: ['schedule:write'] },
  { id: 'power', label: '전원·시작', icon: Power, needs: ['fleet:write'] },
  { id: 'maintenance', label: '정비 PIN', icon: KeyRound, needs: ['fleet:write'] },
];

/**
 * 이 사람이 열 수 있는 메뉴. 하나도 없으면 톱니바퀴 자체를 내지 않는다.
 *
 * 그려 놓고 안에서 "권한이 필요합니다" 를 적으면, 연 사람은 눌러 보고 나서야 자기가 못 한다는
 * 것을 안다.
 */
export function settingsSectionsFor(capabilities: readonly string[]): SettingsSection[] {
  return SETTINGS_SECTIONS.filter((section) => section.needs.some((need) => capabilities.includes(need))).map(
    (section) => section.id,
  );
}

const ROTATIONS: ReadonlyArray<{ value: Display['rotation']; label: string; portrait: boolean; caption: string }> = [
  { value: 0, label: '0°', portrait: false, caption: '가로' },
  { value: 90, label: '90°', portrait: true, caption: '세로 (시계방향으로 세움)' },
  { value: 180, label: '180°', portrait: false, caption: '가로 (위아래 뒤집힘)' },
  { value: 270, label: '270°', portrait: true, caption: '세로 (반시계방향으로 세움)' },
];

/** 설정 한 줄 — 왼쪽 이름(필요하면 물음표), 오른쪽 조작과 그 아래 지금 상태. */
function Row({ label, help, children }: { label: string; help?: string; children: ReactNode }): ReactNode {
  return (
    <div className="setting-row">
      <div className="setting-label">
        {label}
        {help ? <HelpTip text={help} label={`${label} 도움말`} /> : null}
      </div>
      <div className="setting-control">{children}</div>
    </div>
  );
}

/**
 * 소리 — 음소거는 누르는 즉시, 음량은 손을 놓을 때 저장한다(끄는 동안 칸마다 저장하지 않게).
 *
 * 영상의 소리에만 걸린다. 콘텐츠마다 정한 소리 크기와 곱해지고, 기기·TV 의 음량보다 크게는 나오지 않는다.
 */
function AudioRow({
  display,
  pending,
  onPatch,
}: {
  display: Display;
  pending: boolean;
  onPatch: Props['onPatch'];
}): ReactNode {
  const audio = display.audio ?? { volume: 100, muted: false };
  const [volume, setVolume] = useState(audio.volume);
  useEffect(() => setVolume(audio.volume), [audio.volume]);
  const commit = (): void => {
    if (volume !== audio.volume) onPatch({ audio: { volume, muted: audio.muted } });
  };
  return (
    <Row
      label="소리"
      help="영상의 소리입니다. 콘텐츠마다 정한 소리 크기와 곱해지고, 기기·TV 의 음량보다 크게는 나오지 않습니다. 사무실 로비처럼 소리가 나면 안 되는 자리는 음소거로 두세요."
    >
      <div className="seg" role="group" aria-label="음소거">
        <button
          type="button"
          className="seg-btn"
          aria-pressed={!audio.muted}
          disabled={pending}
          onClick={() => {
            if (audio.muted) onPatch({ audio: { volume: audio.volume, muted: false } });
          }}
        >
          켜기
        </button>
        <button
          type="button"
          className="seg-btn"
          aria-pressed={audio.muted}
          disabled={pending}
          onClick={() => {
            if (!audio.muted) onPatch({ audio: { volume: audio.volume, muted: true } });
          }}
        >
          음소거
        </button>
      </div>
      <input
        type="range"
        min={0}
        max={100}
        step={5}
        value={volume}
        disabled={pending || audio.muted}
        aria-label="음량"
        onChange={(event) => setVolume(Number(event.target.value))}
        onPointerUp={commit}
        onKeyUp={commit}
        onBlur={commit}
      />
      <span className="hint">{audio.muted ? '영상이 소리 없이 나갑니다' : `음량 ${volume}%`}</span>
    </Row>
  );
}

const HOURS_MODES: ReadonlyArray<{ mode: 'inherit' | 'always' | 'custom'; label: string; caption: string }> = [
  { mode: 'inherit', label: '환경 설정 따르기', caption: '환경 설정의 운영 시간을 따릅니다(정하지 않았으면 늘 운영).' },
  { mode: 'always', label: '늘 켜기', caption: '운영 시간과 상관없이 늘 편성을 틉니다.' },
  { mode: 'custom', label: '이 화면만', caption: '이 화면만 아래 시간대에 운영합니다.' },
];

/**
 * 운영 시간 — 사옥마다 문 여는 시간이 다르고, 24시간 도는 자리도 있다. 고객사 하나에 운영 시간
 * 하나로는 모자라 화면이 덮는다. '이 화면만' 은 규칙을 적고 [저장] 을 눌러야 저장된다(고르는 즉시
 * 저장하면 규칙 없이 저장돼 거절된다).
 */
function HoursRow({
  display,
  pending,
  onPatch,
}: {
  display: Display;
  pending: boolean;
  onPatch: Props['onPatch'];
}): ReactNode {
  const current = display.hours ?? { mode: 'inherit' as const, rules: [] };
  const [mode, setMode] = useState(current.mode);
  const [rules, setRules] = useState<DaypartInput[]>(
    current.rules.length > 0 ? current.rules : [{ days: [1, 2, 3, 4, 5], start: '07:00', end: '22:00' }],
  );
  const caption = HOURS_MODES.find((option) => option.mode === mode)?.caption ?? '';

  return (
    <Row label="운영 시간" help="운영 시간 밖에는 편성이 있어도 화면이 검게 쉽니다. 긴급 공지와 바로 보내기는 쉬는 시간에도 나갑니다.">
      <div className="seg" role="group" aria-label="운영 시간">
        {HOURS_MODES.map((option) => (
          <button
            key={option.mode}
            type="button"
            className="seg-btn"
            aria-pressed={mode === option.mode}
            disabled={pending}
            onClick={() => {
              setMode(option.mode);
              if (option.mode !== 'custom' && current.mode !== option.mode) onPatch({ hours: { mode: option.mode, rules: [] } });
            }}
          >
            {option.label}
          </button>
        ))}
      </div>
      <span className="hint">{caption}</span>
      {mode === 'custom' ? (
        <div className="mt-2 space-y-2">
          <HoursRulesEditor rules={rules} onChange={setRules} />
          <button
            type="button"
            className="btn btn-primary"
            disabled={pending || rules.length === 0}
            onClick={() => onPatch({ hours: { mode: 'custom', rules } })}
          >
            저장
          </button>
        </div>
      ) : null}
    </Row>
  );
}

interface Props {
  display: Display;
  /** 등록된 에이전트 버전 — 버전 고정의 선택지. */
  versions: readonly VersionOption[];
  capabilities: readonly string[];
  pending: boolean;
  /** 채널·버전 고정·회전·운영 시간·소리 — PATCH /displays/:id. */
  onPatch: (patch: Partial<Pick<Display, 'channel' | 'pinned_version' | 'rotation' | 'hours' | 'audio'>>) => void;
  /** 자동 업데이트·부팅 시 자동 실행. */
  onAgentSettings: (change: { auto_update?: boolean | null; autostart?: boolean | null }) => void;
  onCommand: (kind: AgentCommandKind) => void;
  /** 정비 모드 PIN — null 이면 콘솔 PIN 해제. */
  onPin: (pin: string | null) => void;
}

export function DisplaySettings({
  display,
  versions,
  capabilities,
  pending,
  onPatch,
  onAgentSettings,
  onCommand,
  onPin,
}: Props): ReactNode {
  const sections = settingsSectionsFor(capabilities);
  const [active, setActive] = useState<SettingsSection>(sections[0] ?? 'software');
  const canSchedule = capabilities.includes('schedule:write');
  const canFleet = capabilities.includes('fleet:write');
  const settings = display.agent_control?.settings ?? null;

  /* 위아래 화살표로 메뉴를 옮긴다(세로 탭의 표준 조작). */
  const onNavKey = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    event.preventDefault();
    const at = sections.indexOf(active);
    const next = sections[(at + (event.key === 'ArrowDown' ? 1 : -1) + sections.length) % sections.length];
    if (!next) return;
    setActive(next);
    document.getElementById(`settings-tab-${next}`)?.focus();
  };

  const reason = behindReason(display.agent);
  const rotation = ROTATIONS.find((option) => option.value === (display.rotation ?? 0)) ?? ROTATIONS[0]!;

  return (
    <div className="settings-shell">
      <div className="settings-nav" role="tablist" aria-orientation="vertical" aria-label="화면 설정" onKeyDown={onNavKey}>
        {SETTINGS_SECTIONS.filter((section) => sections.includes(section.id)).map((section) => {
          const Icon = section.icon;
          const on = section.id === active;
          return (
            <button
              key={section.id}
              id={`settings-tab-${section.id}`}
              type="button"
              role="tab"
              aria-selected={on}
              aria-controls={`settings-pane-${section.id}`}
              tabIndex={on ? 0 : -1}
              className={`settings-nav-item ${on ? 'is-on' : ''}`}
              onClick={() => setActive(section.id)}
            >
              <Icon aria-hidden="true" strokeWidth={2} />
              {section.label}
            </button>
          );
        })}
      </div>

      <div
        className="settings-pane"
        role="tabpanel"
        id={`settings-pane-${active}`}
        aria-labelledby={`settings-tab-${active}`}
      >
        {active === 'software' ? (
          <>
            <h4 className="settings-title">소프트웨어</h4>
            <Row label="에이전트 버전">
              <div className="setting-value">
                <span className="mono">{display.status.app_version ?? '보고 없음'}</span>
                {reason ? (
                  <>
                    <Badge tone={reason.tone}>{reason.label}</Badge>
                    <HelpTip text={reason.hint} label="업데이트 상태 도움말" />
                  </>
                ) : display.status.app_version ? (
                  <Badge tone="ok">최신</Badge>
                ) : null}
              </div>
              {display.agent?.wanted ? <span className="hint">목표 버전: v{display.agent.wanted}</span> : null}
            </Row>
            {canSchedule ? (
              <>
                <Row label="업데이트 채널" help="카나리는 새 버전을 먼저 받습니다. 배포할 버전은 에이전트 화면의 롤아웃에서 정합니다.">
                  <div className="seg" role="group" aria-label="업데이트 채널">
                    {(['stable', 'canary'] as const).map((channel) => (
                      <button
                        key={channel}
                        type="button"
                        className="seg-btn"
                        aria-pressed={display.channel === channel}
                        disabled={pending}
                        onClick={() => {
                          if (display.channel !== channel) onPatch({ channel });
                        }}
                      >
                        {channel === 'stable' ? '안정' : '카나리'}
                      </button>
                    ))}
                  </div>
                </Row>
              </>
            ) : null}
            {canFleet ? (
              <>
                <Row
                  label="자동 업데이트"
                  help="켜면 새 버전을 스스로 받아 설치하고, 끄면 현장 정비 화면에서 누를 때만 설치합니다. 콘솔에서 정하면 기기 정비 화면의 스위치가 잠깁니다."
                >
                  <ChoiceSegment
                    value={settings?.auto_update}
                    label="자동 업데이트"
                    disabled={pending}
                    onChange={(next) => onAgentSettings({ auto_update: next })}
                  />
                  <span className="hint">현재 기기: {reportedText(display.agent_control, 'auto_update')}</span>
                </Row>
              </>
            ) : null}
            {/* 맨 끝에 — 드문 설정은 자주 쓰는 칸들 뒤로. */}
            {canSchedule ? (
              <>
                {/*
                  버전 고정은 문제 판을 피할 때만 쓰는 일이라 [고급] 뒤에 접는다(점진적 공개). 고정돼 있으면 펼친 채로 —
                  롤아웃을 따르지 않는 화면이라는 것을 다음 사람이 봐야 한다.
                */}
                <Disclosure
                  title="고급"
                  summary={display.pinned_version ? `v${display.pinned_version} 고정` : '버전 고정 안 함'}
                >
                  <Row
                    label="버전 고정"
                    help="고정한 버전은 채널과 롤아웃보다 우선합니다. 이미 받은 버전보다 낮게 고정해도 되돌아가지 않고 이후 업데이트만 멈춥니다."
                  >
                    <select
                      className="input w-full"
                      aria-label="버전 고정"
                      value={display.pinned_version ?? ''}
                      disabled={pending}
                      onChange={(event) => onPatch({ pinned_version: event.target.value || null })}
                    >
                      <option value="">고정 안 함</option>
                      {versions.map((option) => (
                        <option key={option.version} value={option.version}>
                          v{option.version} · {option.targets.join(' · ')}
                        </option>
                      ))}
                      {/* 등록이 지워진 버전에 고정돼 있어도 지금 상태는 정직하게 보여준다. */}
                      {display.pinned_version && !versions.some((o) => o.version === display.pinned_version) ? (
                        <option value={display.pinned_version}>v{display.pinned_version} (등록되지 않음)</option>
                      ) : null}
                    </select>
                    <span className="hint">
                      {display.pinned_version
                        ? `v${display.pinned_version} 고정 · 롤아웃을 따르지 않음`
                        : `${display.channel === 'canary' ? '카나리' : '안정'} 채널 버전을 받음`}
                    </span>
                  </Row>
                </Disclosure>
              </>
            ) : null}
          </>
        ) : null}

        {active === 'display' ? (
          <>
            <h4 className="settings-title">화면 표시</h4>
            <Row label="화면 회전" help="패널을 실제로 설치한 방향을 고릅니다. 글자가 거꾸로 보이면 90°와 270°를 바꿔 보세요.">
              <div className="seg" role="group" aria-label="화면 회전">
                {ROTATIONS.map((option) => {
                  const Icon = option.portrait ? RectangleVertical : RectangleHorizontal;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      className="seg-btn"
                      aria-pressed={rotation.value === option.value}
                      disabled={pending}
                      onClick={() => {
                        if (rotation.value !== option.value) onPatch({ rotation: option.value });
                      }}
                    >
                      <Icon className="h-3.5 w-3.5" aria-hidden="true" strokeWidth={2} />
                      {option.label}
                    </button>
                  );
                })}
              </div>
              <span className="hint">{rotation.caption}</span>
            </Row>
            <HoursRow display={display} pending={pending} onPatch={onPatch} />
            <AudioRow display={display} pending={pending} onPatch={onPatch} />
          </>
        ) : null}

        {active === 'power' ? (
          <>
            <h4 className="settings-title">
              전원·시작
              <HelpTip text="기기 정비 화면의 [전원·시작] 메뉴와 같은 항목입니다." label="전원·시작 도움말" />
            </h4>
            <AgentOutdatedNotice display={display} />
            <Row label="부팅 시 자동 실행" help="끄면 기기를 켜도 앱이 실행되지 않고 키오스크 고정도 풀립니다.">
              <ChoiceSegment
                value={settings?.autostart}
                label="부팅 시 자동 실행"
                disabled={pending}
                onChange={(next) => onAgentSettings({ autostart: next })}
              />
              <span className="hint">현재 기기: {reportedText(display.agent_control, 'autostart')}</span>
            </Row>
            <Row
              label="원격 명령"
              help="화면이 곧바로(늦어도 30초 안에) 받아 실행하며, 현장 정비 중에는 실행하지 않습니다 — 정비 모드 해제만은 정비 중에 합니다. 앱 종료·전원 종료 뒤에는 현장에서 다시 켜야 합니다."
            >
              <RemoteCommands display={display} pending={pending} onCommand={onCommand} />
            </Row>
          </>
        ) : null}

        {active === 'maintenance' ? (
          <>
            <h4 className="settings-title">
              정비 PIN
              <HelpTip
                text="현장에서 화면을 두 번 눌러 정비 모드를 열 때 입력하는 PIN 입니다. 서버 주소·네트워크는 현장 정비 화면에서만 바꿉니다."
                label="정비 PIN 도움말"
              />
            </h4>
            <Row label="현재 PIN">
              <span className="setting-value">
                {display.maintenance_pin
                  ? `콘솔 PIN · ${new Date(display.maintenance_pin.set_at).toLocaleString('ko-KR', {
                      dateStyle: 'medium',
                      timeStyle: 'short',
                    })}${display.maintenance_pin.set_by ? ` · ${display.maintenance_pin.set_by}` : ''}`
                  : '기기 PIN (기본 0000)'}
              </span>
            </Row>
            <Row label="PIN 변경">
              <MaintenancePinForm
                pending={pending}
                canClear={Boolean(display.maintenance_pin)}
                onSave={(pin) => onPin(pin)}
                onClear={() => onPin(null)}
              />
            </Row>
          </>
        ) : null}
      </div>
    </div>
  );
}
